import Rails from '@rails/ujs';
import { Turbo } from '@hotwired/turbo-rails';
import '../portal/application.scss';
import { InitializationHelpers } from '../portal/portalHelpers';

let initializedBody = null;

export const CHATWOOT_SESSION_RESET_STORAGE_KEY =
  'smartway.chatwoot.session-reset.v1';
const CHATWOOT_SESSION_RESET_SEEN_KEY =
  'smartway.chatwoot.session-reset-seen.v1';
const CHATWOOT_SESSION_RESET_ACK_KEY =
  'smartway.chatwoot.session-reset-ack.v1';
let pendingChatwootSessionResetMarker = null;
let handledChatwootSessionResetMarker = null;

const readStorageItem = (storageName, key) => {
  try {
    return window[storageName]?.getItem(key) || null;
  } catch {
    return null;
  }
};

const acknowledgeChatwootSessionReset = marker => {
  handledChatwootSessionResetMarker = marker;
  try {
    window.sessionStorage?.setItem(
      CHATWOOT_SESSION_RESET_SEEN_KEY,
      marker
    );
  } catch {
    // The in-memory marker prevents a reset loop when storage is unavailable.
  }
  try {
    window.localStorage?.setItem(CHATWOOT_SESSION_RESET_ACK_KEY, marker);
  } catch {
    // Other already-open tabs still process the original storage event. The
    // acknowledgement only prevents future tabs from replaying an old reset.
  }
};

const chatwootSessionResetWasHandled = marker =>
  handledChatwootSessionResetMarker === marker ||
  readStorageItem('sessionStorage', CHATWOOT_SESSION_RESET_SEEN_KEY) === marker;

const resetChatwootForSessionBoundary = () => {
  const marker = pendingChatwootSessionResetMarker;
  if (!marker || chatwootSessionResetWasHandled(marker)) {
    pendingChatwootSessionResetMarker = null;
    return;
  }

  const widget = window.$chatwoot;
  if (!widget || typeof widget.reset !== 'function') {
    return;
  }

  try {
    widget.toggle?.('close');
    widget.reset();
    acknowledgeChatwootSessionReset(marker);
    pendingChatwootSessionResetMarker = null;
  } catch {
    // Keep the marker pending; the next widget-ready event will retry safely.
  }
};

const observeChatwootSessionReset = marker => {
  if (!marker || chatwootSessionResetWasHandled(marker)) {
    return;
  }
  pendingChatwootSessionResetMarker = marker;
  resetChatwootForSessionBoundary();
};

const initializeChatwootSessionBoundary = () => {
  window.addEventListener?.('storage', event => {
    if (
      event.key !== CHATWOOT_SESSION_RESET_STORAGE_KEY ||
      !event.newValue
    ) {
      return;
    }
    observeChatwootSessionReset(event.newValue);
  });
  window.addEventListener?.(
    'chatwoot:ready',
    resetChatwootForSessionBoundary
  );
  const persistedMarker = readStorageItem(
    'localStorage',
    CHATWOOT_SESSION_RESET_STORAGE_KEY
  );
  const acknowledgedMarker = readStorageItem(
    'localStorage',
    CHATWOOT_SESSION_RESET_ACK_KEY
  );
  if (persistedMarker !== acknowledgedMarker) {
    observeChatwootSessionReset(persistedMarker);
  }
};

// Chatwoot portal стартує через Turbo, але Vite module script може
// зареєструвати handler після першого lifecycle event. Дублюємо bootstrap через
// DOMContentLoaded/immediate fallback і запускаємо ініціалізацію один раз на body,
// щоб не множити document-level handlers для locale, links, theme і dropdown.
const initializePortalForCurrentBody = () => {
  if (!document.body || initializedBody === document.body) {
    return;
  }

  initializedBody = document.body;
  InitializationHelpers.onLoad();
};

Rails.start();
initializeChatwootSessionBoundary();

document.addEventListener('turbo:load', initializePortalForCurrentBody);
document.addEventListener('turbo:before-render', () => {
  InitializationHelpers.beforeRender();
  initializedBody = null;
});
document.addEventListener('DOMContentLoaded', initializePortalForCurrentBody);

if (document.readyState !== 'loading') {
  window.setTimeout(initializePortalForCurrentBody, 0);
}

Turbo.start();
